import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet-async';

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  RedditShareButton,
} from 'react-share';
import { ShareGlyph, ShareBubble } from './ShareIcons';

import { NewLoginForm } from './NewLoginDailogForm';
import { BackDropLoad } from './LoadingBackDrop';
import { Loading } from './Loading';
import { UserContext } from './userContext';
import { PageContainer, Protected, LoginRequired } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const I = {
  ArrowLeft: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
};

export const SingleInfo = () => {
  const { id } = useParams();
  const url = window.location.href;
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [infoData, setInfoData] = useState(null);
  const [infoChilds, setInfoChilds] = useState([]);
  const [parent, setParent] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [wait, setWait] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Axios.post('/new_apis/get-enginfo-singleinfo', { id }, { headers: { key } }).then((res) => {
      if (res.data?.status) {
        setInfoData(res.data.desc?.[0] || null);
        setInfoChilds(res.data.childs || []);
        setParent(res.data.parentDetails?.[0] || null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id, user]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <Loading />;

  if (!user) {
    return (
      <>
        <Helmet><title>Sign in required | Engineering Info | ESAY</title></Helmet>
        <BackDropLoad wait={wait} />
        <NewLoginForm
          strict
          strictTitle="Sign in to view Engineering Info"
          strictMessage="Engineering Info is available to ESAY members. Log in to access the full reference library."
          open={showLoginForm}
          showload={() => setWait(true)}
          hideload={() => setWait(false)}
          closeform={() => setShowLoginForm(false)}
          openloginform={() => setShowLoginForm(true)}
        />
        <PageContainer maxWidth="md">
          <LoginRequired
            title="Sign in to view this article"
            message="Engineering Info is available to ESAY members. Sign in to access the full reference library."
            onLogin={() => setShowLoginForm(true)}
          />
        </PageContainer>
      </>
    );
  }

  return (
    <Protected>
      {infoData?.info_child_cat_ids && (
        <Helmet>
          <title>{infoData.info_child_cat_ids} | Engineering Info | ESAY</title>
        </Helmet>
      )}

      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to view Engineering Info"
        strictMessage="Engineering Info is available to ESAY members. Log in to access the full reference library."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      <RouterLink
        to={parent ? `/eng-info/info-detail/${parent.parent_slug}/${parent.parent_id}` : '/eng-info/home'}
        className="
          group inline-flex items-center gap-1.5 mb-3
          text-[11.5px] font-bold uppercase tracking-[0.12em] text-amber
          transition-[gap,color] duration-200
          hover:gap-2.5 hover:text-amber-light
        "
      >
        <I.ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-0.5" />
        Back to {parent ? parent.parent_name : 'Engineering Info'}
      </RouterLink>

      {infoData && (
        <>
          <h1 className="text-[22px] sm:text-[26px] md:text-[28px] font-bold tracking-[-0.022em] text-fg-primary leading-[1.2]">
            {infoData.info_child_cat_ids}
          </h1>
          <div aria-hidden className="mt-2 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />

          <div className="mt-4 mb-4 flex flex-wrap items-center gap-2 pb-3 border-b border-border-subtle">
            <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Share</span>
            <FacebookShareButton url={url}><ShareBubble label="Facebook"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
            <LinkedinShareButton url={url}><ShareBubble label="LinkedIn"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
            <TwitterShareButton url={url}><ShareBubble label="X"><ShareGlyph.X /></ShareBubble></TwitterShareButton>
            <RedditShareButton url={url}><ShareBubble label="Reddit"><ShareGlyph.Reddit /></ShareBubble></RedditShareButton>
            <WhatsappShareButton url={url}><ShareBubble label="WhatsApp"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
            <TelegramShareButton url={url}><ShareBubble label="Telegram"><ShareGlyph.Telegram /></ShareBubble></TelegramShareButton>
            <EmailShareButton url={url}><ShareBubble label="Email"><ShareGlyph.Email /></ShareBubble></EmailShareButton>

            <button
              type="button"
              onClick={handleCopy}
              aria-label={copied ? 'Link copied' : 'Copy link'}
              title={copied ? 'Link copied!' : 'Click to copy the URL'}
              className={`
                inline-flex h-9 w-9 items-center justify-center rounded-full
                border bg-surface-elevated cursor-pointer
                transition-[background-color,border-color,color,transform,box-shadow] duration-200 ease-out-soft
                hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
                hover:shadow-[0_8px_18px_-6px_rgba(251,191,36,0.55)]
                ${copied ? 'border-amber text-amber' : 'border-border-subtle text-fg-secondary'}
              `}
            >
              {copied ? <I.Check className="h-4 w-4" /> : <I.Link className="h-4 w-4" />}
            </button>
          </div>

          {infoData.info_data_desc && (
            <div
              className="
                rounded-xl border p-4 sm:p-5 md:p-6
                text-[14px] leading-[1.7]
                [&_p]:mb-3
                [&_a]:underline [&_a]:underline-offset-2
                [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:my-3
                [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[19px] [&_h1]:font-bold
                [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[17px] [&_h2]:font-bold
                [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:text-[15px] [&_h3]:font-bold
                [&_h4]:mt-3 [&_h4]:mb-2 [&_h4]:text-[14px] [&_h4]:font-bold
                [&_strong]:font-bold
                [&_b]:font-bold
                [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:my-2
                [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:my-2
                [&_li]:my-1
                [&_blockquote]:border-l-[3px] [&_blockquote]:border-amber [&_blockquote]:pl-3 [&_blockquote]:my-3 [&_blockquote]:italic
                [&_table]:w-full [&_table]:my-3 [&_table]:border-collapse
                [&_th]:font-bold [&_th]:p-2 [&_th]:text-left [&_th]:border [&_th]:border-black/10
                [&_td]:p-2 [&_td]:text-left [&_td]:border [&_td]:border-black/10
              "
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: 'rgba(10,10,11,0.08)',
                color: '#1f1f23',
                boxShadow: '0 4px 16px -8px rgba(0,0,0,0.4)',
              }}
            >
              {parse(infoData.info_data_desc)}
            </div>
          )}
        </>
      )}

      {infoChilds.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 inline-flex items-center gap-1.5">
            <span aria-hidden className="inline-flex h-1 w-1 rounded-full bg-amber" />
            <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber">Sub-topics</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {infoChilds.map((item) => (
              <RouterLink
                key={item.info_data_id}
                to={`/eng-info/info-detail/${item.info_data_slug}/${item.info_data_id}`}
                className="
                  group flex items-center justify-between gap-2 rounded-lg
                  border border-border-subtle bg-surface-paper px-3.5 py-3
                  transition-[border-color,background-color,transform] duration-200 ease-out-soft
                  hover:border-amber/45 hover:bg-amber/8 hover:-translate-y-0.5
                "
              >
                <span className="text-[13px] font-semibold text-fg-secondary transition-colors duration-200 group-hover:text-amber">
                  {item.info_child_cat_ids}
                </span>
                <I.ArrowRight className="h-4 w-4 shrink-0 text-fg-tertiary transition-[color,transform] duration-200 group-hover:text-amber group-hover:translate-x-0.5" />
              </RouterLink>
            ))}
          </div>
        </div>
      )}
    </Protected>
  );
};
